import React from "react";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ParticipantCoverageFilters, {
  DatePreset,
} from "./ParticipantCoverageFilters";
import {
  DataCoverageService,
  MissingDataAnalysisResponse,
} from "../../../services/dataCoverageService";
import { apiService } from "../../../config/api";

interface MissingDataAnalysisCardProps {
  participantId: string;
  selectedParticipant: { firstName?: string; lastName?: string } | null;
  onDataChange?: (data: MissingDataAnalysisResponse | null) => void;
}

const MAX_VISIBLE_RANGES = 20;
const ROW_HEIGHT = 36;
const RANGES_MAX_HEIGHT = `${MAX_VISIBLE_RANGES * ROW_HEIGHT}px`;

const MissingDataAnalysisCard: React.FC<MissingDataAnalysisCardProps> = ({
  participantId,
  selectedParticipant,
  onDataChange,
}) => {
  const [fromDate, setFromDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [preset, setPreset] = React.useState<DatePreset | null>("7d");
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] =
    React.useState<MissingDataAnalysisResponse | null>(null);

  // Notify parent when data changes
  React.useEffect(() => {
    if (onDataChange) {
      onDataChange(result);
    }
  }, [result, onDataChange]);
  const [copied, setCopied] = React.useState(false);

  const noCoverageRanges = React.useMemo(() => {
    if (!result) return [];
    return result.noCoverageDateRanges ?? result.placeholderDateRanges ?? [];
  }, [result]);

  React.useEffect(() => {
    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
  }, []);

  React.useEffect(() => {
    setResult(null);
    setError(null);
    setPreset("7d");

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    const start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    setFromDate(start);
    setEndDate(end);
  }, [participantId]);

  const applyPreset = (nextPreset: DatePreset | null) => {
    setPreset(nextPreset);
    if (!nextPreset) return;

    const today = new Date();
    const end = today.toISOString().slice(0, 10);
    let start = new Date(today);

    if (nextPreset === "7d") {
      start = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
    } else if (nextPreset === "1m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 1);
      start.setDate(start.getDate() + 1);
    } else if (nextPreset === "6m") {
      start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      start.setDate(start.getDate() + 1);
    } else {
      start = new Date(today);
      start.setFullYear(start.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
    }

    setFromDate(start.toISOString().slice(0, 10));
    setEndDate(end);
  };

  const handleRun = async () => {
    if (!participantId || !fromDate || !endDate) {
      setError("Please select both from and end dates.");
      return;
    }

    try {
      setRunning(true);
      setError(null);
      setResult(null);

      const token = await apiService.getAuthToken();
      if (!token) {
        setError("Sign in to run missing data analysis.");
        return;
      }

      const response = await DataCoverageService.getMissingDataAnalysis(
        participantId,
        {
          fromDate,
          endDate,
          preset,
          refreshIfMissing: true,
        },
        token,
      );

      if (response.success && (response as any).data) {
        setResult((response as any).data);
      } else {
        setError("Failed to run missing data analysis.");
      }
    } catch (err: any) {
      setError(err?.message || "Error while running missing data analysis.");
    } finally {
      setRunning(false);
    }
  };

  const handleCopyReport = async () => {
    if (!result) return;

    const lines: string[] = [];
    lines.push(`Days with coverage: ${result.summary.daysWithCoverage}`);
    lines.push(
      `Days without coverage (all common metrics are 0, null, or missing): ${result.summary.daysWithoutCoverage}`,
    );
    lines.push(`Coverage: ${result.summary.coveragePct.toFixed(1)}%`);
    lines.push("");
    lines.push("No-coverage date ranges:");

    if (noCoverageRanges.length === 0) {
      lines.push("  None");
    } else {
      noCoverageRanges.forEach((range) => {
        lines.push(
          `  ${range.startDate} to ${range.endDate} (${range.dayCount} days)`,
        );
      });
    }

    await navigator.clipboard.writeText(lines.join("\n"));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2.5}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Missing Data Analysis
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ParticipantCoverageFilters
            disabled={!selectedParticipant}
            fromDate={fromDate}
            endDate={endDate}
            preset={preset}
            onFromDateChange={(value) => {
              setPreset(null);
              setFromDate(value);
            }}
            onEndDateChange={(value) => {
              setPreset(null);
              setEndDate(value);
            }}
            onPresetChange={applyPreset}
            onRun={handleRun}
            running={running}
          />
        </Box>

        {running && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}

        {result?.fetch?.failedFitbitRefreshRanges &&
          result.fetch.failedFitbitRefreshRanges.length > 0 && (
            <Alert severity="warning">
              Fitbit data could not be retrieved for:{" "}
              {result.fetch.failedFitbitRefreshRanges
                .map((range) => `${range.startDate} to ${range.endDate}`)
                .join(", ")}
            </Alert>
          )}

        {result && !running && (
          <Stack spacing={2}>
            <Paper
              variant="outlined"
              sx={{ p: 2, backgroundColor: "#faf7f2", borderColor: "#e7d8c6" }}
            >
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                Days without coverage (all common metrics are 0, null, or
                missing): {result.summary.daysWithoutCoverage}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ mt: 0.5 }}
              >
                Days with coverage: {result.summary.daysWithCoverage}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Coverage: {result.summary.coveragePct.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Key metrics checked: {result.keyMetrics.join(", ")}
              </Typography>
            </Paper>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                No-Coverage Date Ranges ({noCoverageRanges.length})
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleCopyReport}
              >
                {copied ? "Copied" : "Copy Report"}
              </Button>
            </Stack>

            <Box
              sx={{
                maxHeight: RANGES_MAX_HEIGHT,
                overflowY: "auto",
                border: "1px solid #eee",
                borderRadius: 1.5,
                p: 1,
                backgroundColor: "#fff",
              }}
            >
              {noCoverageRanges.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                  No no-coverage date ranges found in this period.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {noCoverageRanges.map((range) => (
                    <Paper
                      key={`${range.startDate}-${range.endDate}`}
                      variant="outlined"
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderColor: "#e5e5e5",
                        backgroundColor: "#fcfcfc",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {range.startDate} to {range.endDate}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {range.dayCount} day{range.dayCount === 1 ? "" : "s"}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default MissingDataAnalysisCard;
